import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, getApiClient } from '@/lib/api'
import type {
  IssueMcpCredentialInput,
  OnboardGiftOperatorInput,
  UpdateMeetingSourceInput,
} from '@/types/gift-operations'

const GIFT_OPERATIONS_KEYS = {
  all: ['gift-operations'] as const,
  operators: ['gift-operations', 'operators'] as const,
  sources: ['gift-operations', 'meeting-sources'] as const,
  imports: ['gift-operations', 'meeting-imports'] as const,
  approvalTarget: ['gift-operations', 'meeting-import-approval-target'] as const,
  audit: ['gift-operations', 'audit-events'] as const,
}

export function useGiftOperators(workspaceId: string) {
  return useQuery({
    queryKey: [...GIFT_OPERATIONS_KEYS.operators, workspaceId],
    queryFn: () => getApiClient().listGiftOperators(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: 30_000,
  })
}

export function useOnboardGiftOperator(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: OnboardGiftOperatorInput) =>
      getApiClient().onboardGiftOperator(workspaceId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.operators })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.audit })
    },
  })
}

export function useOffboardGiftOperator(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (operatorId: string) => getApiClient().offboardGiftOperator(workspaceId, operatorId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.all })
    },
  })
}

export function useIssueGiftMcpCredential(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: IssueMcpCredentialInput) =>
      getApiClient().issueGiftMcpCredential(workspaceId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.operators })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.audit })
    },
  })
}

export function useReissueGiftMcpCredential(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (credentialId: string) =>
      getApiClient().reissueGiftMcpCredential(workspaceId, credentialId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.operators })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.audit })
    },
  })
}

export function useRevokeGiftMcpCredential(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (credentialId: string) =>
      getApiClient().revokeGiftMcpCredential(workspaceId, credentialId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.all })
    },
  })
}

export function useMeetingSources(workspaceId: string) {
  return useQuery({
    queryKey: [...GIFT_OPERATIONS_KEYS.sources, workspaceId],
    queryFn: () => getApiClient().listMeetingSources(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: 30_000,
  })
}

export function useUpdateMeetingSource(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sourceId, input }: { sourceId: string; input: UpdateMeetingSourceInput }) =>
      getApiClient().updateMeetingSource(workspaceId, sourceId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.sources })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.audit })
    },
  })
}

export function useTestMeetingSource(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sourceId: string) => getApiClient().testMeetingSource(workspaceId, sourceId),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.sources })
    },
  })
}

export function useSyncMeetingImports(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { sourceConfigId: string; provider: 'google_meet' | 'zoom_meeting'; externalMeetingId: string; externalArtifactId: string }) =>
      getApiClient().syncMeetingImports(workspaceId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.imports })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.audit })
    },
  })
}

export function useMeetingImports(workspaceId: string) {
  return useQuery({
    queryKey: [...GIFT_OPERATIONS_KEYS.imports, workspaceId],
    queryFn: () => getApiClient().listMeetingImports(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: 15_000,
  })
}

export function useMeetingImportApprovalTarget(workspaceId: string) {
  return useQuery({
    queryKey: [...GIFT_OPERATIONS_KEYS.approvalTarget, workspaceId],
    queryFn: () => getApiClient().getMeetingImportApprovalTarget(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: 15_000,
  })
}

export function useApproveMeetingImport(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      importId: string
      expectedWeekOf: string
      expectedSnapshotId: string
      expectedRevision: number
      targetWorkstreamId: string
    }) => {
      const { importId, ...expected } = input
      return getApiClient().approveMeetingImport(workspaceId, importId, expected)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.all })
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.approvalTarget })
      await queryClient.invalidateQueries({ queryKey: ['meeting-center', 'latest', workspaceId] })
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: [...GIFT_OPERATIONS_KEYS.approvalTarget, workspaceId],
        })
      }
    },
  })
}

export function useRejectMeetingImport(workspaceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (importId: string) => getApiClient().rejectMeetingImport(workspaceId, importId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: GIFT_OPERATIONS_KEYS.all })
    },
  })
}

export function useGiftAuditEvents(workspaceId: string) {
  return useQuery({
    queryKey: [...GIFT_OPERATIONS_KEYS.audit, workspaceId],
    queryFn: () => getApiClient().listGiftAuditEvents(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: 15_000,
  })
}
